import type { NewCertificateRequestWizardStep } from "@/hooks/useNewCertificateRequestFlow";

type EventProps = Record<string, string | number | boolean | null>;

type MagSdk = {
  track: (name: string, props?: EventProps) => void;
  identify: (id: string, traits?: EventProps) => void;
  getDistinctId: () => string | null;
  flush: () => Promise<void>;
};

declare global {
  interface Window {
    mag?: MagSdk;
  }
}

/**
 * UTM attribution. SessionStorage (not localStorage) so attribution resets
 * between independent visits. Captured once per session on app mount; every
 * `trackEvent` call merges the stored values onto the payload so all
 * conversion-adjacent events carry attribution without per-call wiring.
 * Empty / missing keys are never sent.
 */
export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];

/**
 * Where on OUR OWN site a visitor came from — e.g. `tramite:certificado-de-domicilio`
 * for the bespoke landing page of that trámite.
 *
 * Deliberately NOT a `utm_*` param. Those describe an EXTERNAL campaign, and
 * stamping one on an internal link overwrites the real acquisition source: a
 * visitor who arrived from Google Ads and then clicked through from a trámite
 * page would have their ad attribution replaced by our own hop. This sits
 * beside the UTMs instead, so a request can say both "came from an ad" and
 * "started on this page".
 */
export const INTERNAL_SOURCE_KEY = "src";

/**
 * Google click identifiers, forwarded onto the portal URL by the landing's
 * TrackedLink. `gclid` is the ordinary one; `gbraid` / `wbraid` replace it on
 * iOS app and web-to-app journeys. Exactly one is ever present, so an arriving
 * id clears its siblings.
 *
 * Captured here as well as on the landing, and for the same reason it is
 * captured there: the id arrives on the URL once and the events that matter
 * fire several navigations later. `payment_initiated` is a PORTAL event at the
 * end of the wizard — reading the click id off the live URL at that point finds
 * nothing, which is exactly how 11 of 13 of them came to carry no gclid.
 */
export const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid"] as const;

export type ClickIdKey = (typeof CLICK_ID_KEYS)[number];

export function captureAttributionToSession(): void {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of [...UTM_KEYS, INTERNAL_SOURCE_KEY]) {
      const value = params.get(key);
      if (value != null && value.trim() !== "") {
        window.sessionStorage.setItem(key, value);
      }
    }

    // The click ids are mutually exclusive: a link carrying two is
    // attributable to neither, so an arriving id replaces rather than joins.
    // Captured as a key/value pair rather than re-read: the id is read once,
    // and the write needs no assertion that it is still there.
    let arriving: { key: ClickIdKey; value: string } | null = null;
    for (const key of CLICK_ID_KEYS) {
      const value = params.get(key);
      if (value != null && value.trim() !== "") {
        arriving = { key, value: value.trim() };
        break;
      }
    }
    if (arriving != null) {
      for (const key of CLICK_ID_KEYS) {
        if (key === arriving.key) {
          window.sessionStorage.setItem(key, arriving.value);
        } else {
          window.sessionStorage.removeItem(key);
        }
      }
    }
  } catch {
    // sessionStorage can throw in private / quota-exceeded modes.
  }
}

/**
 * The stored internal source, or null.
 *
 * Read at submit time rather than passed down through the wizard: the value
 * has to survive a login round-trip that leaves and re-enters the app, and
 * sessionStorage already survives it where component state does not.
 */
export function readInternalSource(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const value = window.sessionStorage.getItem(INTERNAL_SOURCE_KEY);
    return value != null && value.trim() !== "" ? value.trim().slice(0, 120) : null;
  } catch {
    return null;
  }
}

/**
 * The visitor's Magig browser distinct id — the identity Magig keys on-site
 * events by. Threaded to the backend at checkout creation so the server-fired
 * `request_paid` conversion stitches to the same visitor's funnel instead of
 * landing under our internal user id. Null before the mag SDK has assigned an
 * id (brand-new session, or mag blocked) — callers must send/omit gracefully.
 */
export function readMagDistinctId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const id = window.mag?.getDistinctId();
    return id != null && id.trim() !== "" ? id : null;
  } catch {
    return null;
  }
}

function readStored<K extends string>(keys: readonly K[]): Partial<Record<K, string>> {
  if (typeof window === "undefined") return {};
  const out: Partial<Record<K, string>> = {};
  try {
    for (const key of keys) {
      const value = window.sessionStorage.getItem(key);
      if (value != null && value.trim() !== "") {
        out[key] = value;
      }
    }
  } catch {
    // ignore
  }
  return out;
}

export function readStoredUtms(): Partial<Record<UtmKey, string>> {
  return readStored(UTM_KEYS);
}

/**
 * The captured click id, as a 0-or-1 entry object.
 *
 * Merged onto every tracked event alongside the UTMs. It is NOT sent to our own
 * backend: nothing server-side consumes a click id today (see
 * MagAnalyticsService — we do not report the payment as a conversion), and a
 * column nothing reads is a migration with no reader.
 */
export function readStoredClickIds(): Partial<Record<ClickIdKey, string>> {
  return readStored(CLICK_ID_KEYS);
}

/**
 * Wizard step → public snake_case name + funnel index.
 * Indices reflect funnel position (branched steps at the same depth share an index).
 * Names are stable contracts with analytics; never rename.
 */
export const WIZARD_STEPS: Record<
  NewCertificateRequestWizardStep,
  { name: string; index: number }
> = {
  chooseIntent: { name: "choose_intent", index: 1 },
  chooseType: { name: "choose_type", index: 2 },
  quoteRequest: { name: "quote_request", index: 2 },
  chooseEnterpriseProduct: { name: "choose_enterprise_product", index: 3 },
  legalEntityDetails: { name: "legal_entity_details", index: 3 },
  incomeCertificate: { name: "income_certificate", index: 3 },
  preconditionCheck: { name: "precondition_check", index: 4 },
  fillEnterpriseProduct: { name: "fill_enterprise_product", index: 4 },
  workflowSteps: { name: "workflow_steps", index: 5 },
};

export function trackEvent(name: string, props?: EventProps): void {
  if (typeof window === "undefined") return;
  try {
    const utms = readStoredUtms();
    const clickIds = readStoredClickIds();
    const internalSource = readInternalSource();
    const attribution: EventProps = {
      ...utms,
      ...clickIds,
      ...(internalSource != null ? { [INTERNAL_SOURCE_KEY]: internalSource } : {}),
    };
    const hasAttribution = Object.keys(attribution).length > 0;
    const merged: EventProps | undefined =
      props != null || hasAttribution ? { ...(props ?? {}), ...attribution } : undefined;
    window.mag?.track(name, merged);
  } catch {
    // Swallow — analytics must never break the page.
  }
}

/**
 * De-dupe store for once-per-visit events, mirroring the idiom in
 * `lib/google-ads.ts`. A SEPARATE key from that file's: the two systems fire
 * on different moments and sharing one key would let a Google Ads conversion
 * silently suppress a Magig one, or the reverse.
 *
 * Returns true the first time a scope is seen and false afterwards; a throwing
 * sessionStorage (private mode, quota) returns true, because under-counting a
 * conversion is worse than the occasional double.
 */
const CONVERSION_FIRED_KEY = "gestionalo.mag.fired";

function markConversionFired(scope: string): boolean {
  try {
    const stored = sessionStorage.getItem(CONVERSION_FIRED_KEY) ?? "";
    const tokens = stored ? stored.split(",") : [];
    if (tokens.includes(scope)) return false;
    tokens.push(scope);
    sessionStorage.setItem(CONVERSION_FIRED_KEY, tokens.join(","));
    return true;
  } catch {
    return true;
  }
}

/**
 * THE CONVERSION. A visitor came from the landing, entered the RFQ wizard, and
 * committed by describing what they need in their own words.
 *
 * ONE EVENT, NOT TWO. Registering and sending the first message were briefly
 * specced as separate events; they are the same moment for the visitor this
 * measures. A new arrival sends their first message, hits the auth gate on
 * submit, and registers minutes later in the same session — counting both
 * would double every genuine conversion. They diverge only in cases that are
 * NOT this conversion: an already-registered consumer opening another RFQ, and
 * a professional signing up with no RFQ at all.
 *
 * THE MESSAGE, NOT THE REGISTRATION, is the one kept: it is the earlier signal
 * and does not depend on auth timing, so it still fires for a returning
 * consumer who never sees a registration screen.
 *
 * Google Ads still optimises on its own registration conversion, which is
 * unchanged and deliberately not wired to this — see `lib/google-ads.ts`.
 *
 * NOT THE MESSAGE CONTENT. `message_length` is a volume signal; what the
 * visitor typed is their business and is already in the request.
 */
export function trackRfqFirstMessageSent(messageLength: number): void {
  if (!markConversionFired("rfq_first_message")) return;
  trackEvent("rfq_first_message_sent", { message_length: messageLength });
}

/**
 * THE CONVERSION: an RFQ that actually exists.
 *
 * WHY THIS AND NOT `rfq_first_message_sent`, which held the title until
 * 2026-10-13. That one fires when the consumer types their first line into the
 * intake chat — the moment they start talking to the assistant, not the moment
 * there is anything for a professional to bid on. Someone who sends one
 * message, reads the reply and leaves counted as a conversion. This fires only
 * after `createQuoteRequest` resolves, when the row exists.
 *
 * ITS PREDECESSOR IS KEPT, DEMOTED. `rfq_first_message_sent` is now a funnel
 * step rather than the goal, and it earns its place there: it is the only thing
 * that separates "reached the chat and never typed" from "typed and never
 * submitted". `wizard_step_viewed` cannot see that difference, because the step
 * stays `quote_request` across the whole conversation. Deleting it would have
 * traded one blind spot for another.
 *
 * NOT DE-DUPED, and that is the one place it deliberately differs from its
 * neighbour. `trackRfqFirstMessageSent` guards with `markConversionFired` once
 * per session; a submission must not, because a consumer can legitimately
 * submit two RFQs in one visit and both are real conversions. The mutation
 * resolving is already the once-per-RFQ guarantee — there is no second success
 * for one request to double-count.
 *
 * `from_intake` IS THE PROPERTY THAT EARNS ITS PLACE. The intake conversation
 * is the expensive half of this product, and this says whether it actually
 * produces submissions or whether people abandon it and fill the fallback form
 * instead. `leg_count` would be the obvious thing to send and is deliberately
 * absent: how many disciplines a request fans out to is decided by triage on
 * the server, so the client would have to guess it.
 *
 * No title, no description: what the consumer wrote is their business and is
 * already in the request.
 */
export function trackRfqSubmitted(input: {
  fromIntake: boolean;
  attachmentCount: number;
}): void {
  trackEvent("rfq_submitted", {
    from_intake: input.fromIntake,
    attachment_count: input.attachmentCount,
  });
}

/*
  THE STEP BETWEEN THE TWO ABOVE (spec rfq-auth-gate-instrumentation).

  `rfq_first_message_sent` and `rfq_submitted` were the whole RFQ funnel, and
  the auth gate sits between them emitting nothing — so "typed and never
  submitted" could not be split into "hit the wall and left" and "never
  finished the conversation", which have opposite fixes. Mag's own funnel read
  over MCP on 2026-10-16 stopped dead at `rfq_first_message_sent` and flagged it
  as the biggest leak; it could not see what happened to the six who got past it.

  WHY THREE EVENTS AND NOT A COUNTER. The shown/dismissed pair is the bounce,
  and `completed` is what makes it a RATE rather than a count — without it a
  quiet week and a week where everyone converted look identical.

  NOT DE-DUPED, unlike `trackRfqFirstMessageSent`. A visitor who closes the
  modal and tries again has bounced twice and both are real; one IP started
  three guest conversations in 11 minutes on 2026-10-14. Uniques are a
  query-time question Magig can answer, and collapsing them at emit time
  destroys information that cannot be recovered.

  `from_intake` IS THE SAME PROP `rfq_submitted` SENDS, deliberately: the chat
  is the expensive half of the product, and comparing its bounce against the
  fallback form's is the comparison worth having.
*/

/** The wall appeared in front of an anonymous visitor. */
export function trackRfqAuthGateShown(input: { fromIntake: boolean }): void {
  trackEvent("rfq_auth_gate_shown", { from_intake: input.fromIntake });
}

/**
 * They authenticated through it.
 *
 * `auth_path` is the modal's own discriminator, passed straight through, so
 * this says WHICH route past the wall worked — a wall that only returning
 * users clear is a different problem from one nobody clears.
 */
export function trackRfqAuthGateCompleted(input: {
  fromIntake: boolean;
  authPath: string;
}): void {
  trackEvent("rfq_auth_gate_completed", {
    from_intake: input.fromIntake,
    auth_path: input.authPath,
  });
}

/**
 * They closed it without authenticating. THE BOUNCE — the number this whole
 * spec exists to produce.
 */
export function trackRfqAuthGateDismissed(input: { fromIntake: boolean }): void {
  trackEvent("rfq_auth_gate_dismissed", { from_intake: input.fromIntake });
}
